let googleMapsLoadPromise: Promise<void> | null = null;

const loadGoogleMapsApi = (apiKey: string): Promise<void> => {
    if (
        typeof window !== "undefined" &&
        typeof google !== "undefined" &&
        google.maps &&
        google.maps.importLibrary
    ) {
        return Promise.resolve();
    }

    if (googleMapsLoadPromise) {
        return googleMapsLoadPromise;
    }

    googleMapsLoadPromise = new Promise<void>((resolve, reject) => {
        const callbackName = "__marineOneGoogleMapsInit";
        const globalWindow = (window as unknown) as Window & {
            [key: string]: unknown;
        };

        const existingImportLibrary = window.google?.maps?.importLibrary;
        if (existingImportLibrary) {
            Promise.all([
                existingImportLibrary("maps"),
                existingImportLibrary("geometry"),
                existingImportLibrary("marker"),
            ])
                .then(() => resolve())
                .catch((error) => {
                    googleMapsLoadPromise = null;
                    reject(error);
                });
            return;
        }

        globalWindow[callbackName] = () => {
            Promise.all([
                google.maps.importLibrary("maps"),
                google.maps.importLibrary("geometry"),
                google.maps.importLibrary("marker"),
            ])
                .then(() => {
                    delete globalWindow[callbackName];
                    resolve();
                })
                .catch((error) => {
                    googleMapsLoadPromise = null;
                    reject(error);
                });
        };

        const script = document.createElement("script");
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            apiKey
        )}&libraries=geometry,marker&loading=async&callback=${callbackName}&v=weekly`;
        script.onerror = () => {
            googleMapsLoadPromise = null;
            delete globalWindow[callbackName];
            reject(new Error("The Google Maps JavaScript API could not load."));
        };

        document.head.appendChild(script);
    }).catch((error) => {
        googleMapsLoadPromise = null;
        throw error;
    });

    return googleMapsLoadPromise;
};

export default function () {
    const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY;

    if (!apiKey) {
        throw new Error(
            "Missing VUE_APP_GOOGLE_MAPS_API_KEY. Set it in a local .env file."
        );
    }

    const googleMapApi = {
        load: () => loadGoogleMapsApi(apiKey),
    };

    return { googleMapApi };
}